/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

/** Matches timestamps that already include a UTC offset or `Z`. */
const EXPLICIT_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i

/**
 * Captures the first three fractional-second digits and drops the rest,
 * even when followed by a timezone offset.
 */
const OVERLONG_FRACTION = /(\.\d{3})\d+/

const DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
})

const TIME_FORMAT = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
})

const MINUTE = 60
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY

// Approximate by design — the UI displays rounded relative times,
// not calendar-accurate durations.
const MONTH = 30 * DAY
const YEAR = 365 * DAY

const INTERVALS: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: "year", seconds: YEAR },
    { unit: "month", seconds: MONTH },
    { unit: "week", seconds: WEEK },
    { unit: "day", seconds: DAY },
    { unit: "hour", seconds: HOUR },
    { unit: "minute", seconds: MINUTE }
]

/**
 * Use `numeric: "always"` because relative times are computed from elapsed
 * seconds. Calendar labels such as `yesterday` and `last week` may not match
 * the actual calendar date.
 */
const RELATIVE_FORMAT = new Intl.RelativeTimeFormat("en-GB", { numeric: "always" })

function asUtcString(timestamp: string): string {
    // Truncate fractional seconds to three digits regardless of whether an offset
    // is present. Longer fractions rely on implementation-defined parsing.
    const trimmed = timestamp.replace(OVERLONG_FRACTION, "$1")

    return EXPLICIT_OFFSET.test(trimmed) ? trimmed : `${trimmed}Z`
}

/**
 * Parses an API timestamp into a `Date`, treating zone-less timestamps as UTC.
 *
 * @remarks
 * The API serializes `LocalDateTime` values without a timezone designator
 * (e.g. `2026-07-01T15:46:18`). JavaScript interprets that format as local
 * time, so a `Z` is appended when no offset is present.
 *
 * Returns `undefined` for missing or invalid timestamps.
 */
function parseTimestamp(timestamp?: string | number): Date | undefined {
    if (!timestamp) return undefined

    const date = new Date(typeof timestamp === "number" ? timestamp : asUtcString(timestamp))
    return Number.isNaN(date.getTime()) ? undefined : date
}

/**
 * Formats a timestamp for display in the local timezone.
 *
 * @param timestamp - ISO timestamp string or epoch milliseconds.
 * @returns A date/time string such as `25/05/2025 14:34:33`, or an empty string
 *   when the timestamp is missing or invalid.
 *
 * @remarks
 * Timestamps without an explicit timezone are treated as UTC.
 */
export function formatTimestamp(timestamp?: string | number): string {
    const date = parseTimestamp(timestamp)
    if (!date) return ""

    return `${DATE_FORMAT.format(date)} ${TIME_FORMAT.format(date)}`
}

/**
 * Formats a timestamp as a relative time string.
 *
 * @param timestamp - ISO timestamp string or epoch milliseconds.
 * @returns A human-readable relative time such as `5 minutes ago`,
 *   `2 days ago`, or `just now`. Returns an empty string when the
 *   timestamp is missing or invalid.
 *
 * @remarks
 * Timestamps without an explicit timezone are treated as UTC.
 */
export function timeAgo(timestamp?: string | number): string {
    const past = parseTimestamp(timestamp)
    if (!past) return ""

    const now = new Date()
    const seconds = Math.floor((now.getTime() - past.getTime()) / 1000)

    for (const interval of INTERVALS) {
        const count = Math.floor(seconds / interval.seconds)
        if (count >= 1) {
            return RELATIVE_FORMAT.format(-count, interval.unit)
        }
    }

    return "just now"
}
